import type { ReactElement } from 'react';
import type { WorkspaceGuest, WorkspaceGuestInviteRule } from '@molt/shared';
import { Avatar, Button, Notice, Skeleton } from '@/components';
import { profileName } from '@/components/Avatar';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface WorkspaceGuestAccessSectionProps {
  guestInviteRule: WorkspaceGuestInviteRule | undefined;
  guests: readonly WorkspaceGuest[] | undefined;
  canManage: boolean;
  loading?: boolean | undefined;
  ruleBusy?: boolean | undefined;
  removeBusyId?: string | null | undefined;
  ruleError?: string | undefined;
  guestErrors?: Readonly<Record<string, string>> | undefined;
  onChangeGuestInviteRule: (rule: WorkspaceGuestInviteRule) => void;
  onRemoveGuest: (userId: string) => void;
}

/**
 * ⚠ WIRE VALUES, not copy. `GUEST_INVITE_RULES` is the set
 * `WorkspaceGuestInviteRule` declares and the value handed to
 * `onChangeGuestInviteRule` — it stays literal in every language.
 */
const GUEST_INVITE_RULES = ['anyone', 'workspace_admins_only', 'nobody'] as const;

/**
 * FE-80.1 — CATALOGUE KEYS, not English.
 *
 * ⚠ A module-level constant cannot call `t()` (it has no context), so each
 * table holds the KEY and every read resolves it at render. Holding the
 * English of the moment would leave the previous language on screen after the
 * catalogue query resolves, and nothing in this repository can see that.
 *
 * ⚠ THE TWO RECORDS POINT AT TWO DIFFERENT NAMESPACES, ON PURPOSE, and this is
 * the asymmetry most likely to be "tidied" later. The three rule LABELS are the
 * words `routes/w.$workspaceId.members.tsx` already SPEAKS through
 * `workspaceMembers.guests.rule.*` ("Guest invite rule set to {rule}."), and
 * the catalogue says so in a comment at those very keys: this file DRAWS what
 * that route SPEAKS, so it REUSES those three keys rather than declaring a
 * fourth `Anyone`. The DESCRIPTIONS are drawn only here and are this slice's
 * own, under `workspaceSettings.*`. Collapsing either into the other lets the
 * word the page speaks and the word the control draws disagree in every
 * language but English.
 *
 * ⚠ `Record<WorkspaceGuestInviteRule, string>` stays EXPLICIT on both. It is
 * the only thing making a missing or misspelled arm a typecheck error: `t()`
 * returns the KEY on a miss rather than throwing, so a dropped arm would render
 * `workspaceSettings.guests.ruleDescription.nobody` into the page with nothing
 * red. The RECORDS' KEYS are wire values; only the VALUES are catalogue keys.
 */
const GUEST_RULE_KEYS: Record<WorkspaceGuestInviteRule, string> = {
  anyone: 'workspaceMembers.guests.rule.anyone',
  workspace_admins_only: 'workspaceMembers.guests.rule.adminsOnly',
  nobody: 'workspaceMembers.guests.rule.nobody',
};

const GUEST_RULE_DESCRIPTION_KEYS: Record<WorkspaceGuestInviteRule, string> = {
  anyone: 'workspaceSettings.guests.ruleDescription.anyone',
  workspace_admins_only: 'workspaceSettings.guests.ruleDescription.adminsOnly',
  nobody: 'workspaceSettings.guests.ruleDescription.nobody',
};

export function WorkspaceGuestAccessSection({
  guestInviteRule,
  guests,
  canManage,
  loading = false,
  ruleBusy = false,
  removeBusyId = null,
  ruleError,
  guestErrors = {},
  onChangeGuestInviteRule,
  onRemoveGuest,
}: WorkspaceGuestAccessSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <section className="flex flex-col gap-4" aria-labelledby="guest-access-heading">
      <div className="flex flex-col gap-1">
        <h2
          id="guest-access-heading"
          className="font-display text-lg font-semibold text-ink"
        >
          {t('workspaceSettings.guests.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('workspaceSettings.guests.description')}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-base font-semibold text-ink">
          {t('workspaceSettings.guests.ruleHeading')}
        </h3>
        <div
          className="grid gap-2 md:grid-cols-3"
          role="group"
          aria-label={t('workspaceSettings.guests.ruleHeading')}
        >
          {GUEST_INVITE_RULES.map((rule) => {
            const selected = guestInviteRule === rule;
            const disabledReason = guestRuleDisabledReason(t, {
              canManage,
              guestInviteRule,
              ruleBusy,
              selected,
            });
            const ruleLabel = t(GUEST_RULE_KEYS[rule]);

            return (
              <Button
                key={rule}
                type="button"
                variant="toggle"
                pressed={selected}
                disabled={disabledReason !== undefined}
                disabledReason={disabledReason}
                busy={ruleBusy && selected}
                aria-label={t('workspaceSettings.guests.ruleControl', {
                  rule: ruleLabel,
                })}
                onClick={() => onChangeGuestInviteRule(rule)}
                className="h-auto min-h-9 justify-start py-2 text-left"
              >
                <span className="flex min-w-0 flex-col items-start gap-0.5">
                  <span className="text-card text-current">{ruleLabel}</span>
                  <span className="text-xs font-normal text-current opacity-80">
                    {t(GUEST_RULE_DESCRIPTION_KEYS[rule])}
                  </span>
                </span>
              </Button>
            );
          })}
        </div>
        {ruleError !== undefined ? <Notice tone="bad" body={ruleError} /> : null}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-base font-semibold text-ink">
          {t('workspaceSettings.guests.listHeading')}
        </h3>

        {loading ? (
          <div role="status" className="flex flex-col gap-2 text-sm text-ink-muted">
            <span>{t('workspaceSettings.guests.loading')}</span>
            <Skeleton shape="row" count={3} />
          </div>
        ) : null}

        {guests !== undefined && !loading && guests.length === 0 ? (
          <p role="status" className="text-sm text-ink-muted">
            {t('workspaceSettings.guests.empty')}
          </p>
        ) : null}

        {guests !== undefined && guests.length > 0 ? (
          <ul
            role="list"
            aria-label={t('workspaceSettings.guests.listLabel')}
            className="flex flex-col gap-2"
          >
            {guests.map((guest) => (
              <GuestRow
                key={guest.id}
                guest={guest}
                canManage={canManage}
                removeBusyId={removeBusyId}
                error={guestErrors[guest.id]}
                onRemoveGuest={onRemoveGuest}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

function GuestRow({
  guest,
  canManage,
  removeBusyId,
  error,
  onRemoveGuest,
}: {
  guest: WorkspaceGuest;
  canManage: boolean;
  removeBusyId: string | null;
  error: string | undefined;
  onRemoveGuest: (userId: string) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const name = profileName(guest);
  const removingThisGuest = removeBusyId === guest.id;
  const disabledReason = !canManage
    ? t('workspaceSettings.guests.removeLocked')
    : removeBusyId !== null && !removingThisGuest
      ? t('workspaceSettings.guests.removeBusy')
      : undefined;

  return (
    <li role="listitem" className="flex flex-col gap-2 border-b border-border py-2">
      <div className="flex items-center gap-2">
        <Avatar profile={guest} size="sm" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-ink">{name}</span>
          {guest.username !== '' && guest.displayName !== '' ? (
            <span className="text-xs text-ink-muted">@{guest.username}</span>
          ) : null}
          <span className="text-xs text-ink-muted">
            {/* ⚠ `boardCount` is a PLURAL key and the third argument is NOT
                optional: `interfaceMessage` selects `forms.one` only when the
                third argument is 1, and never reads the count out of `values`.
                Dropping it renders "1 boards" at every arity. */}
            {t(
              'workspaceSettings.guests.boardCount',
              {
                type: t(
                  guest.guestType === 'multi_board'
                    ? 'workspaceSettings.guests.type.multiBoard'
                    : 'workspaceSettings.guests.type.singleBoard',
                ),
                count: guest.boardCount,
              },
              guest.boardCount,
            )}
          </span>
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabledReason !== undefined}
          disabledReason={disabledReason}
          busy={removingThisGuest}
          aria-label={t('workspaceSettings.guests.removeControl', { name })}
          onClick={() => onRemoveGuest(guest.id)}
        >
          {t('workspaceSettings.guests.remove')}
        </Button>
      </div>

      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
    </li>
  );
}

/**
 * ⚠ Takes `t` as its FIRST parameter rather than returning a key. A module
 * helper has no context, and its three arms are three different sentences, so
 * the caller would otherwise have to resolve a `string | undefined` key — the
 * `boardFilterLabelFormatter(t)` precedent.
 */
function guestRuleDisabledReason(
  t: InterfaceTranslator,
  {
    canManage,
    guestInviteRule,
    ruleBusy,
    selected,
  }: {
    canManage: boolean;
    guestInviteRule: WorkspaceGuestInviteRule | undefined;
    ruleBusy: boolean;
    selected: boolean;
  },
): string | undefined {
  if (!canManage) return t('workspaceSettings.guests.ruleLocked');
  if (guestInviteRule === undefined) return t('workspaceSettings.guests.rulePending');
  if (ruleBusy && !selected) return t('workspaceSettings.guests.ruleBusy');
  return undefined;
}
